/**
 * CriticTab — run persona-based critique against a chosen document scope.
 *
 * The user picks a scope from a dropdown ("Whole Document", "Selected Text",
 * or a titled content control such as "Study Rationale") and one or more
 * personas. Section and selection scopes send the full document as context plus
 * a target section; whole-document scope sends the body text only. Critique
 * jobs run in parallel, polling until complete, then render results.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Alert,
  AlertDescription,
  Badge,
  Label,
  Separator,
} from '@e14c/feathers';
import {
  Renew,
  ChevronDown,
  ChevronUp,
  CheckmarkFilled,
  WarningAlt,
  Information,
} from '@e14c/feathers/icons';

import { useCriticStore } from '../store';
import type { CritiqueRun } from '../store/critic';
import {
  WHOLE_DOCUMENT_SCOPE,
  SELECTED_TEXT_SCOPE,
  SELECTED_PASSAGE_TITLE,
} from '../store/critic';
import { listPersonas, submitCritique, pollCritique } from '../api/critic';
import type { CritiqueContent, CritiqueResult, Recommendation } from '../types';
import { useWordSelection } from '../hooks/useWordSelection';
import { useTabLayoutStyles } from '../styles/tabLayout';
import { TabScrollArea } from '../components/TabScrollArea';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const Spinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <span
    style={{ width: size, height: size }}
    className="inline-block animate-spin rounded-full border-2 border-muted-foreground border-t-transparent"
  />
);

function scoreColorClass(score: number): string {
  if (score >= 7) return 'bg-green-100 text-green-800 border-green-300';
  if (score >= 5) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  return 'bg-red-100 text-red-800 border-red-300';
}

function priorityBadgeVariant(
  priority: Recommendation['priority'],
): 'destructive' | 'secondary' | 'outline' {
  if (priority === 'high') return 'destructive';
  if (priority === 'medium') return 'secondary';
  return 'outline';
}

// ---------------------------------------------------------------------------
// Office.js document scope helpers
// ---------------------------------------------------------------------------

/** Distinct, trimmed titles of the document's content controls. */
async function loadContentControlTitles(): Promise<string[]> {
  if (typeof Word === 'undefined' || typeof Word.run !== 'function') return [];
  return Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load('title');
    await context.sync();
    const titles = contentControls.items
      .map((cc) => cc.title?.trim() ?? '')
      .filter((title) => title.length > 0);
    return Array.from(new Set(titles));
  });
}

/** Scroll to a content control by title and select all of its content. */
async function selectContentControlByTitle(title: string): Promise<void> {
  if (typeof Word === 'undefined' || typeof Word.run !== 'function') return;
  await Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load('title');
    await context.sync();
    const match = contentControls.items.find(
      (cc) => cc.title?.trim() === title,
    );
    if (!match) return;
    // Selecting the content control scrolls it into view and highlights its
    // contents so the user can see what will be critiqued.
    match.select();
    await context.sync();
  });
}

/**
 * Resolve preview text for the given scope: full body, a content control
 * section, or the current Word selection.
 */
async function resolveScopeText(scope: string): Promise<string | null> {
  if (typeof Word === 'undefined' || typeof Word.run !== 'function') {
    return null;
  }
  return Word.run(async (context) => {
    if (scope === WHOLE_DOCUMENT_SCOPE) {
      const body = context.document.body;
      body.load('text');
      await context.sync();
      const text = body.text?.trim() ?? '';
      return text.length > 0 ? text : null;
    }
    if (scope === SELECTED_TEXT_SCOPE) {
      const selection = context.document.getSelection();
      selection.load('text');
      await context.sync();
      const text = selection.text?.trim() ?? '';
      return text.length > 0 ? text : null;
    }
    const contentControls = context.document.contentControls;
    contentControls.load('title, text');
    await context.sync();
    const match = contentControls.items.find(
      (cc) => cc.title?.trim() === scope,
    );
    const text = match?.text?.trim() ?? '';
    return text.length > 0 ? text : null;
  });
}

async function extractFullDocumentText(): Promise<string | null> {
  if (typeof Word === 'undefined' || typeof Word.run !== 'function') {
    return null;
  }
  return Word.run(async (context) => {
    const body = context.document.body;
    body.load('text');
    await context.sync();
    const text = body.text?.trim() ?? '';
    return text.length > 0 ? text : null;
  });
}

async function buildCritiqueContent(
  scope: string,
  selectionText?: string | null,
): Promise<CritiqueContent | null> {
  const document = await extractFullDocumentText();
  if (!document) return null;

  if (scope === WHOLE_DOCUMENT_SCOPE) {
    return { document };
  }

  if (scope === SELECTED_TEXT_SCOPE) {
    const passage = selectionText?.trim() ?? '';
    if (!passage) return null;
    return {
      document,
      sections: [{ title: SELECTED_PASSAGE_TITLE, content: passage }],
    };
  }

  const sectionText = await resolveScopeText(scope);
  if (!sectionText) return null;
  return {
    document,
    sections: [{ title: scope, content: sectionText }],
  };
}

// ---------------------------------------------------------------------------
// Collapsible section
// ---------------------------------------------------------------------------

const CollapsibleSection: React.FC<{
  title: string;
  count?: number;
  icon?: React.ReactNode;
  defaultOpen?: boolean;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}> = ({
  title,
  count,
  icon,
  defaultOpen = true,
  headerExtra,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 bg-muted/30 hover:bg-muted/50 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {icon}
        <span className="flex-1 text-sm font-semibold">{title}</span>
        {headerExtra}
        {count !== undefined && <Badge variant="secondary">{count}</Badge>}
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="px-3 py-2 flex flex-col gap-2">{children}</div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Results display
// ---------------------------------------------------------------------------

const CritiqueResultView: React.FC<{ result: CritiqueResult }> = ({
  result,
}) => (
  <div className="flex flex-col gap-3">
    {/* Score + summary */}
    <div className="flex items-start gap-3">
      <span
        className={`shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full border-2 text-xl font-bold ${scoreColorClass(result.score)}`}
      >
        {result.score.toFixed(1)}
      </span>
      <p className="text-sm text-foreground leading-snug pt-1">
        {result.summary}
      </p>
    </div>

    {/* Strengths */}
    {result.strengths.length > 0 && (
      <CollapsibleSection
        title="Strengths"
        count={result.strengths.length}
        icon={<CheckmarkFilled size={16} className="text-green-600 shrink-0" />}
      >
        {result.strengths.map((item, i) => (
          <div key={i} className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">{item.title}</span>
            <span className="text-xs text-muted-foreground">{item.detail}</span>
          </div>
        ))}
      </CollapsibleSection>
    )}

    {/* Weaknesses */}
    {result.weaknesses.length > 0 && (
      <CollapsibleSection
        title="Weaknesses"
        count={result.weaknesses.length}
        icon={<WarningAlt size={16} className="text-yellow-600 shrink-0" />}
      >
        {result.weaknesses.map((item, i) => (
          <div key={i} className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">{item.title}</span>
            <span className="text-xs text-muted-foreground">{item.detail}</span>
          </div>
        ))}
      </CollapsibleSection>
    )}

    {/* Recommendations */}
    {result.recommendations.length > 0 && (
      <CollapsibleSection
        title="Recommendations"
        count={result.recommendations.length}
        icon={<Information size={16} className="text-blue-600 shrink-0" />}
      >
        {result.recommendations.map((rec, i) => (
          <div key={i} className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <Badge variant={priorityBadgeVariant(rec.priority)}>
                {rec.priority}
              </Badge>
              <span className="text-sm font-medium">{rec.title}</span>
            </div>
            <span className="text-xs text-muted-foreground pl-0.5">
              {rec.detail}
            </span>
          </div>
        ))}
      </CollapsibleSection>
    )}
  </div>
);

const CritiqueRunView: React.FC<{ run: CritiqueRun }> = ({ run }) => {
  if (run.status === 'pending' || run.status === 'running') {
    return (
      <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
        <Spinner size={14} />
        <span>
          {run.status === 'pending' ? 'Queued…' : 'Running critique…'}
        </span>
      </div>
    );
  }

  if (run.status === 'failed') {
    return (
      <Alert variant="destructive">
        <AlertDescription>{run.error ?? 'Critique failed'}</AlertDescription>
      </Alert>
    );
  }

  if (!run.job?.result) {
    return (
      <p className="text-sm text-muted-foreground italic">
        Critique completed with no result.
      </p>
    );
  }

  return <CritiqueResultView result={run.job.result} />;
};

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

export const CriticTab: React.FC = () => {
  const layout = useTabLayoutStyles();

  const personas = useCriticStore((s) => s.personas);
  const isLoadingPersonas = useCriticStore((s) => s.isLoadingPersonas);
  const personasError = useCriticStore((s) => s.personasError);
  const selectedPersonaIds = useCriticStore((s) => s.selectedPersonaIds);
  const selectedText = useCriticStore((s) => s.selectedText);
  const criticScope = useCriticStore((s) => s.criticScope);
  const contentControlTitles = useCriticStore((s) => s.contentControlTitles);
  const critiquing = useCriticStore((s) => s.critiquing);
  const critiqueRuns = useCriticStore((s) => s.critiqueRuns);

  const setPersonas = useCriticStore((s) => s.setPersonas);
  const setIsLoadingPersonas = useCriticStore((s) => s.setIsLoadingPersonas);
  const setPersonasError = useCriticStore((s) => s.setPersonasError);
  const setSelectedPersonaIds = useCriticStore((s) => s.setSelectedPersonaIds);
  const togglePersonaSelection = useCriticStore((s) => s.togglePersonaSelection);
  const setSelectedText = useCriticStore((s) => s.setSelectedText);
  const setCriticScope = useCriticStore((s) => s.setCriticScope);
  const setContentControlTitles = useCriticStore(
    (s) => s.setContentControlTitles,
  );
  const setCritiquing = useCriticStore((s) => s.setCritiquing);
  const setCritiqueRuns = useCriticStore((s) => s.setCritiqueRuns);
  const updateCritiqueRun = useCriticStore((s) => s.updateCritiqueRun);

  useWordSelection({
    setSelectedText,
    enabled: criticScope === SELECTED_TEXT_SCOPE && !critiquing,
  });

  // ── Scope tracking ─────────────────────────────────────────────────────────

  // Load the available content-control titles, falling back to whole-document
  // scope if the previously selected section no longer exists.
  const refreshContentControls = useCallback(async () => {
    try {
      const titles = await loadContentControlTitles();
      setContentControlTitles(titles);
      if (
        criticScope !== WHOLE_DOCUMENT_SCOPE &&
        criticScope !== SELECTED_TEXT_SCOPE &&
        !titles.includes(criticScope)
      ) {
        setCriticScope(WHOLE_DOCUMENT_SCOPE);
      }
    } catch {
      // transient Office.js errors — leave the existing titles in place
    }
  }, [setContentControlTitles, setCriticScope, criticScope]);

  useEffect(() => {
    void refreshContentControls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve the preview text whenever the chosen scope changes.
  useEffect(() => {
    if (criticScope === SELECTED_TEXT_SCOPE) return;

    let cancelled = false;
    void (async () => {
      try {
        const text = await resolveScopeText(criticScope);
        if (!cancelled) setSelectedText(text);
      } catch {
        if (!cancelled) setSelectedText(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [criticScope, setSelectedText]);

  const handleScopeChange = async (scope: string) => {
    setCriticScope(scope);
    if (
      scope !== WHOLE_DOCUMENT_SCOPE &&
      scope !== SELECTED_TEXT_SCOPE
    ) {
      try {
        await selectContentControlByTitle(scope);
      } catch {
        // ignore transient Office.js errors while navigating
      }
    }
  };

  // ── Load personas ──────────────────────────────────────────────────────────

  const loadPersonas = useCallback(async () => {
    setIsLoadingPersonas(true);
    setPersonasError(null);
    try {
      const resp = await listPersonas(50, 0);
      if (!resp.data)
        throw new Error(resp.error?.message ?? 'Failed to load personas');
      setPersonas(resp.data.personas);
      if (resp.data.personas.length > 0 && selectedPersonaIds.length === 0) {
        setSelectedPersonaIds([resp.data.personas[0].id]);
      }
    } catch (err: unknown) {
      setPersonasError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoadingPersonas(false);
    }
  }, [
    setIsLoadingPersonas,
    setPersonasError,
    setPersonas,
    setSelectedPersonaIds,
    selectedPersonaIds.length,
  ]);

  useEffect(() => {
    if (personas.length === 0 && !isLoadingPersonas) void loadPersonas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Run critique ───────────────────────────────────────────────────────────

  const runOneCritique = async (personaId: string, content: CritiqueContent) => {
    updateCritiqueRun(personaId, { status: 'running' });

    try {
      const resp = await submitCritique({
        persona_id: personaId,
        content,
      });
      if (!resp.data) {
        throw new Error(resp.error?.message ?? 'Failed to submit critique');
      }

      const job = await pollCritique(resp.data.job_id, (partial) =>
        updateCritiqueRun(personaId, { job: partial }),
      );
      updateCritiqueRun(personaId, { status: 'completed', job });
    } catch (err: unknown) {
      updateCritiqueRun(personaId, {
        status: 'failed',
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const runCritique = async () => {
    if (selectedPersonaIds.length === 0) return;

    // Re-read scope content at run time so edits made after selecting the
    // scope are included.
    let content: CritiqueContent | null = null;
    try {
      content = await buildCritiqueContent(
        criticScope,
        criticScope === SELECTED_TEXT_SCOPE ? selectedText : undefined,
      );
    } catch {
      content = null;
    }
    if (!content) return;

    const previewText =
      content.sections?.[0]?.content ?? content.document ?? null;
    setSelectedText(previewText);

    const runs: CritiqueRun[] = selectedPersonaIds.map((personaId) => {
      const persona = personas.find((p) => p.id === personaId);
      return {
        personaId,
        personaName: persona?.name ?? 'Unknown persona',
        status: 'pending',
        job: null,
        error: null,
      };
    });

    setCritiquing(true);
    setCritiqueRuns(runs);

    await Promise.allSettled(
      selectedPersonaIds.map((personaId) =>
        runOneCritique(personaId, content),
      ),
    );

    setCritiquing(false);
  };

  const selectedCount = selectedPersonaIds.length;
  const canRun = !!selectedText && selectedCount > 0 && !critiquing;
  const completedCount = critiqueRuns.filter(
    (run) => run.status === 'completed' || run.status === 'failed',
  ).length;

  const runButtonLabel = critiquing
    ? selectedCount > 1
      ? `Running ${completedCount}/${selectedCount} critiques…`
      : 'Running critique…'
    : selectedCount > 1
      ? `Run ${selectedCount} Critiques`
      : 'Run Critique';

  return (
    <div className={layout.panel}>
      <TabScrollArea>
        {/* ── Critique scope ─────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Critique scope</Label>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => void refreshContentControls()}
              disabled={critiquing}
              title="Refresh document sections"
            >
              <Renew size={14} />
            </Button>
          </div>
          <select
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
            value={criticScope}
            disabled={critiquing}
            onChange={(e) => void handleScopeChange(e.target.value)}
          >
            <option value={WHOLE_DOCUMENT_SCOPE}>Whole Document</option>
            <option value={SELECTED_TEXT_SCOPE}>Selected Text</option>
            {contentControlTitles.map((title) => (
              <option key={title} value={title}>
                {title}
              </option>
            ))}
          </select>
          <div className="mt-1.5">
            {selectedText ? (
              <p className="text-xs bg-muted rounded px-2 py-1.5 text-foreground/70 break-words line-clamp-4">
                {selectedText.length > 200
                  ? selectedText.slice(0, 200) + '…'
                  : selectedText}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                {criticScope === WHOLE_DOCUMENT_SCOPE
                  ? 'The document appears to be empty.'
                  : criticScope === SELECTED_TEXT_SCOPE
                    ? 'Highlight text in the document to critique.'
                    : 'This section has no content yet.'}
              </p>
            )}
          </div>
        </div>

        <Separator />

        {/* ── Persona picker ─────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Critic personas</Label>
            <div className="flex items-center gap-1">
              {personas.length > 0 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={critiquing || selectedCount === personas.length}
                    onClick={() =>
                      setSelectedPersonaIds(personas.map((p) => p.id))
                    }
                  >
                    All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={critiquing || selectedCount === 0}
                    onClick={() => setSelectedPersonaIds([])}
                  >
                    None
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={loadPersonas}
                disabled={isLoadingPersonas}
                title="Refresh personas"
              >
                <Renew size={14} />
              </Button>
            </div>
          </div>

          {personasError && (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>{personasError}</span>
                <Button variant="ghost" size="sm" onClick={loadPersonas}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {isLoadingPersonas ? (
            <div className="flex items-center gap-2 py-1">
              <Spinner size={14} />
              <span className="text-xs text-muted-foreground">
                Loading personas…
              </span>
            </div>
          ) : personas.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              No critic personas defined yet. Create one in the Manage tab.
            </p>
          ) : (
            <div className="flex flex-col gap-1 border border-border rounded-md p-2 max-h-48 overflow-y-auto">
              {personas.map((persona) => {
                const checked = selectedPersonaIds.includes(persona.id);
                return (
                  <label
                    key={persona.id}
                    className={`flex items-start gap-2 rounded px-2 py-1.5 cursor-pointer hover:bg-muted/50 ${
                      checked ? 'bg-muted/30' : ''
                    } ${critiquing ? 'opacity-60 pointer-events-none' : ''}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 shrink-0"
                      checked={checked}
                      disabled={critiquing}
                      onChange={() => togglePersonaSelection(persona.id)}
                    />
                    <span className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-sm font-medium leading-snug">
                        {persona.name}
                      </span>
                      {persona.description && (
                        <span className="text-xs text-muted-foreground leading-snug">
                          {persona.description}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Run button ─────────────────────────────────────────────────── */}
        <Button
          onClick={() => void runCritique()}
          disabled={!canRun}
          className="w-full"
        >
          {critiquing ? <Spinner size={14} /> : null}
          {runButtonLabel}
        </Button>

        {/* ── Results ────────────────────────────────────────────────────── */}
        {critiqueRuns.length > 0 && (
          <>
            <Separator />
            <div className="flex flex-col gap-3">
              {critiqueRuns.map((run) => (
                <CollapsibleSection
                  key={run.personaId}
                  title={run.personaName}
                  defaultOpen={critiqueRuns.length === 1}
                  headerExtra={
                    run.job?.result ? (
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full border text-xs font-bold ${scoreColorClass(run.job.result.score)}`}
                      >
                        {run.job.result.score.toFixed(1)}
                      </span>
                    ) : run.status === 'running' || run.status === 'pending' ? (
                      <Spinner size={14} />
                    ) : run.status === 'failed' ? (
                      <Badge variant="destructive">failed</Badge>
                    ) : null
                  }
                >
                  <CritiqueRunView run={run} />
                </CollapsibleSection>
              ))}
            </div>
          </>
        )}
      </TabScrollArea>
    </div>
  );
};
